(function (window, document) {
    "use strict";

    // ============================================================================
    // CONSTANTS
    // ============================================================================
    const CONFIG = {
        STRIPE_PUBLIC_KEY: 'pk_live_51Kw35YGFjL7b1JZWcEtyjTzSs17coIgO0LoXqW71npkmYUR2hgi7IUX2lBFGI2c2Sx3RkChKTuJcgQnMwDezE9Qv00O9dVi9MQ',
        API_BASE_URL: 'https://api-service.mrgrien.com/api',
        RETURN_URL: 'https://mrgrien.com?success=true',
        ANIMATION_DELAYS: {
            // Minimal delay needed for CSS transitions to trigger (browser repaint)
            SHORT: 10,
            // Match CSS transition duration (0.3s = 300ms, using 300 for accuracy)
            MEDIUM: 300
        }
    };

    const PRODUCT_CONFIG = {
        'prod_M2lBfuTgeemNqN': {
            validZips: new Set(['8001', '8002', '8003', '8004', '8005', '8006', '8008', '8032', '8037', '8038', '8041', '8044', '8045', '8046', '8047', '8048', '8049', '8050', '8051', '8052', '8053', '8055', '8057', '8064']),
            productTitle: 'Plastik Recycling Stadt Zürich'
        },
        'prod_M2l6s3SlFahaOa': {
            validZips: new Set(['8620', '8623', '8624', '5400', '5405', '5408']),
            productTitle: 'Plastik Recycling'
        }
    };

    const SELECTORS = {
        ZIP_SHIPPING: '#zip',
        ZIP_BILLING: '#address2-zip',
        CITY_SHIPPING: '#city',
        CITY_BILLING: '#address2-city',
        INVALID_ZIP: '.invalid-zip-selected',
        SELECT_BILLING: '.select-billing',
        FORM_NAV_BTN: '.form-navigation > .form-navigation__btn',
        CHECKOUT_FORM: '#checkoutForm',
        PRICES_LIST: '.stripe-prices-list',
        PAYMENT_METHOD_1: '#payment-method-1',
        PAYMENT_METHOD_2: '#payment-method-2',
        AGREE_BTN: '#agree-btn',
        FINISH_BTN: '.finish-btn button',
        CLOSE_POPUP: '.js-close-popup',
        OTHER_ADDRESS_BTN: '.js-other-address',
        ONE_ADDRESS_BTN: '.js-one-address',
        OTHER_ADDRESS: '.other-address',
        OTHER_ADDRESS_INNER: '.other-address__inner',
        WAIT_LOADER: '#wait-loader',
        SUCCESS_LOADER: '#success-loader',
        PAYMENT_FORM: '#payment-form',
        PAYMENT_ELEMENT: '#payment-element',
        ERROR_MESSAGE: '#error-message'
    };

    const REQUIRED_FIELDS = [
        '#address2-name',
        '#address2-nachname',
        '#address2-address',
        '#address2-zip',
        '#address2-city'
    ];

    // ============================================================================
    // UTILITY CLASSES
    // ============================================================================

    /**
     * DOM Manipulation Utility
     */
    class DOMUtils {
        static query(selector) {
            return document.querySelector(selector);
        }

        static queryAll(selector) {
            return Array.from(document.querySelectorAll(selector));
        }

        static addClass(element, className) {
            this._forEachElement(element, el => el?.classList.add(className));
        }

        static removeClass(element, className) {
            this._forEachElement(element, el => el?.classList.remove(className));
        }

        static toggleClass(element, className, condition) {
            condition ? this.addClass(element, className) : this.removeClass(element, className);
        }

        static show(element, displayType = 'block') {
            this._forEachElement(element, el => {
                if (el) {
                    // Use setProperty with important to override CSS rules
                    el.style.setProperty('display', displayType, 'important');
                }
            });
        }

        static hide(element) {
            this._forEachElement(element, el => el && (el.style.display = 'none'));
        }

        static fadeIn(element, options = {}) {
            if (!element) return;
            element.style.display = options.display || 'flex';
            element.style.opacity = '0';
            element.style.transition = 'opacity 0.3s';
            setTimeout(() => {
                element.style.opacity = '1';
                options.done?.();
            }, CONFIG.ANIMATION_DELAYS.SHORT);
        }

        static fadeOut(element, options = {}) {
            if (!element) return;
            element.style.opacity = '0';
            element.style.transition = 'opacity 0.3s';
            setTimeout(() => {
                element.style.display = 'none';
                options.done?.();
            }, CONFIG.ANIMATION_DELAYS.MEDIUM);
        }

        static slideDown(element) {
            if (!element) return;
            element.style.display = 'block';
            element.style.maxHeight = '0';
            element.style.overflow = 'hidden';
            element.style.transition = 'max-height 0.3s ease-out';
            setTimeout(() => {
                element.style.maxHeight = element.scrollHeight + 'px';
            }, CONFIG.ANIMATION_DELAYS.SHORT);
        }

        static slideUp(element) {
            if (!element) return;
            element.style.maxHeight = element.scrollHeight + 'px';
            element.style.overflow = 'hidden';
            element.style.transition = 'max-height 0.3s ease-out';
            setTimeout(() => {
                element.style.maxHeight = '0';
                setTimeout(() => element.style.display = 'none', CONFIG.ANIMATION_DELAYS.MEDIUM);
            }, CONFIG.ANIMATION_DELAYS.SHORT);
        }

        static triggerEvent(element, eventType) {
            if (!element) return;
            element.dispatchEvent(new Event(eventType, { bubbles: true }));
        }

        static _forEachElement(element, callback) {
            if (Array.isArray(element) || element instanceof NodeList) {
                element.forEach(callback);
            } else if (element) {
                callback(element);
            }
        }
    }

    /**
     * String Utilities
     */
    class StringUtils {
        static getUrlParameter(param) {
            return new URLSearchParams(window.location.search).get(param) || false;
        }

        static slugify(text) {
            const charMap = {
                'ã': 'a', 'à': 'a', 'á': 'a', 'ä': 'a', 'â': 'a',
                'ẽ': 'e', 'è': 'e', 'é': 'e', 'ë': 'e', 'ê': 'e',
                'ì': 'i', 'í': 'i', 'ï': 'i', 'î': 'i',
                'õ': 'o', 'ò': 'o', 'ó': 'o', 'ö': 'o', 'ô': 'o',
                'ù': 'u', 'ú': 'u', 'ü': 'u', 'û': 'u',
                'ñ': 'n', 'ç': 'c',
                '·': '-', '/': '-', '_': '-', ':': '-', ';': '-'
            };

            return text
                .split('')
                .map(char => charMap[char] || char)
                .join('')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/&/g, '-y-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-+/g, '-');
        }
    }

    /**
     * Form Utilities
     */
    class FormUtils {
        static serialize(form) {
            const formData = new FormData(form);
            const params = new URLSearchParams();
            for (const [key, value] of formData.entries()) {
                params.append(key, value);
            }
            return params.toString();
        }

        static setRequired(fields, required = true) {
            fields.forEach(selector => {
                const field = DOMUtils.query(selector);
                if (field) {
                    field[required ? 'setAttribute' : 'removeAttribute']('required', required);
                }
            });
        }
    }

    // ============================================================================
    // API SERVICE
    // ============================================================================

    /**
     * API Service - Handles all API communications
     */
    class ApiService {
        constructor(baseUrl) {
            this.baseUrl = baseUrl;
        }

        async get(endpoint, params = {}) {
            const url = new URL(`${this.baseUrl}${endpoint}`);
            Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
            
            console.log(`API GET: ${url.toString()}`);
            
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    }
                });
                
                if (!response.ok) {
                    throw new Error(`API Error: ${response.status} ${response.statusText}`);
                }
                
                const data = await response.json();
                console.log(`API Response:`, data);
                return data;
            } catch (error) {
                console.error(`API GET Error for ${url.toString()}:`, error);
                throw error;
            }
        }

        async post(endpoint, data) {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: data
            });
            
            const responseData = await response.json();
            
            if (!response.ok) {
                // Handle error response - API returns ErrorResource with message field
                const errorMessage = responseData?.message || responseData?.error || `API Error: ${response.status}`;
                const error = new Error(errorMessage);
                error.status = response.status;
                error.data = responseData;
                throw error;
            }
            
            return responseData;
        }

        async getPostalCities() {
            return this.get('/system/postal-cities');
        }

        async getPrices(productId) {
            return this.get('/system/prices', { productId });
        }

        async processCheckout(formData) {
            return this.post('/checkout/process', formData);
        }

        async getPaymentMethod(customerId) {
            return this.get('/system/payment-method', { customerId });
        }
    }

    // ============================================================================
    // ANIMATION MANAGER
    // ============================================================================

    /**
     * Animation Manager - Handles loader animations
     */
    class AnimationManager {
        constructor() {
            this.animations = {};
            this.init();
        }

        init() {
            if (typeof bodymovin === 'undefined') return;

            const waitLoader = DOMUtils.query(SELECTORS.WAIT_LOADER);
            const successLoader = DOMUtils.query(SELECTORS.SUCCESS_LOADER);

            if (waitLoader) {
                this.animations.wait = bodymovin.loadAnimation({
                    container: waitLoader,
                    renderer: 'svg',
                    loop: true,
                    autoplay: false,
                    path: 'https://assets2.lottiefiles.com/packages/lf20_ff6cugbf.json'
                });
            }

            if (successLoader) {
                this.animations.success = bodymovin.loadAnimation({
                    container: successLoader,
                    renderer: 'svg',
                    loop: false,
                    autoplay: false,
                    path: 'https://assets8.lottiefiles.com/packages/lf20_yxxw2ita.json'
                });
                this.animations.success.setSpeed(1.8);
            }
        }

        showWaitLoader() {
            const loader = DOMUtils.query(SELECTORS.WAIT_LOADER);
            if (loader) {
                DOMUtils.fadeIn(loader, { display: 'block' });
                this.animations.wait?.goToAndPlay(0, true);
            }
        }

        hideWaitLoader() {
            const loader = DOMUtils.query(SELECTORS.WAIT_LOADER);
            if (loader) {
                loader.style.setProperty('display', 'none', 'important');
                this.animations.wait?.goToAndStop(0, true);
            }
        }

        showSuccessLoader(callback) {
            const loader = DOMUtils.query(SELECTORS.SUCCESS_LOADER);
            if (loader) {
                DOMUtils.fadeIn(loader, { display: 'block' });
                this.animations.success?.goToAndPlay(0, true);
                if (callback) callback();
            }
        }

        hideSuccessLoader() {
            const loader = DOMUtils.query(SELECTORS.SUCCESS_LOADER);
            if (loader) {
                loader.style.setProperty('display', 'none', 'important');
                this.animations.success?.goToAndStop(0, true);
            }
        }
    }

    // ============================================================================
    // FORM NAVIGATION MANAGER
    // ============================================================================

    /**
     * Form Navigation Manager - Handles multi-step form navigation
     */
    class FormNavigationManager {
        constructor(form) {
            this.form = form;
            // Get all step sections, but exclude step-finish (it's not a form step)
            const allSteps = DOMUtils.queryAll('#checkoutForm .step');
            this.sections = Array.from(allSteps).filter(step => !step.classList.contains('step-finish'));
            this.currentIndex = 0;
            
            console.log('FormNavigationManager initialized with', this.sections.length, 'sections');
            this.sections.forEach((section, i) => {
                console.log(`Section ${i}:`, section.className);
            });
            
            if (!this.form) {
                console.error('Form not found for FormNavigationManager');
                return;
            }
            
            if (this.sections.length === 0) {
                console.error('No form sections found');
                return;
            }
            
            this.init();
        }

        init() {
            this.setupParsley();
            this.setupNavigationButtons();
            this.prepareSections();
            this.navigateTo(0);
        }

        setupParsley() {
            if (typeof window.Parsley === 'undefined' || !this.form) {
                console.warn('Parsley not available, form validation will be limited');
                return;
            }

            try {
                // Parsley.js 2.9.2 initialization - use jQuery if available, otherwise use new Parsley()
                let parsleyInstance = null;
                const externalLib = window.jQuery;
                
                // Try jQuery method first (most common for Parsley 2.x)
                if (externalLib && externalLib.fn && typeof externalLib.fn.parsley === 'function') {
                    try {
                        parsleyInstance = externalLib(this.form).parsley();
                        console.log('Parsley initialized via jQuery');
                    } catch (e) {
                        console.warn('Failed to initialize Parsley via jQuery:', e);
                    }
                }
                
                // If jQuery method didn't work, try direct Parsley methods
                if (!parsleyInstance && window.Parsley) {
                    // Check if Parsley has a constructor function
                    if (typeof window.Parsley === 'function') {
                        try {
                            parsleyInstance = new window.Parsley(this.form);
                            console.log('Parsley initialized via constructor');
                        } catch (e) {
                            console.warn('Failed to initialize Parsley via constructor:', e);
                        }
                    }
                    
                    // Check if Parsley has a factory method (and it's actually a function)
                    if (!parsleyInstance && window.Parsley.factory && typeof window.Parsley.factory === 'function') {
                        try {
                            parsleyInstance = window.Parsley.factory(this.form);
                            console.log('Parsley initialized via factory');
                        } catch (e) {
                            console.warn('Failed to initialize Parsley via factory:', e);
                        }
                    }
                }
                
                if (!parsleyInstance) {
                    console.warn('Could not initialize Parsley - no valid method found. Parsley available:', !!window.Parsley, 'jQuery available:', !!externalLib);
                    return;
                }
                
                if (!parsleyInstance) {
                    console.warn('Parsley instance is null');
                    return;
                }
                
                // Configure Parsley options
                if (parsleyInstance.options) {
                    parsleyInstance.options = Object.assign(parsleyInstance.options || {}, {
                        errorClass: 'parsley-error-1',
                        successClass: 'parsley-success43',
                        classHandler: (field) => {
                            if (!field.isValid()) {
                                const element = field.$element?.[0] || field.element;
                                DOMUtils.addClass(element?.closest('.field'), 'error');
                            }
                        },
                        errorsContainer: () => {},
                        errorTemplate: '<span></span>',
                        errorMessage: 'Diese Eingabe scheint falsch. Bitte ohne Leerzeichen/Abstände.'
                    });
                }

                // Set up event listeners
                if (parsleyInstance.on && typeof parsleyInstance.on === 'function') {
                    parsleyInstance.on('field:success', (field) => {
                        if (field.isValid()) {
                            const element = field.$element?.[0] || field.element;
                            DOMUtils.removeClass(element?.closest('.field'), 'error');
                        }
                    });
                    
                    parsleyInstance.on('field:error', (field) => {
                        if (!field.isValid()) {
                            const element = field.$element?.[0] || field.element;
                            DOMUtils.addClass(element?.closest('.field'), 'error');
                        }
                    });
                }
                
                // Store instance for later use
                this.parsleyInstance = parsleyInstance;
                console.log('Parsley setup completed successfully');
            } catch (error) {
                console.error('Error setting up Parsley:', error);
                console.error('Error details:', {
                    message: error.message,
                    stack: error.stack,
                    parsleyAvailable: typeof window.Parsley !== 'undefined',
                    jqueryAvailable: typeof window.jQuery !== 'undefined'
                });
            }
        }

        setupNavigationButtons() {
            const prevBtn = DOMUtils.query('.form-navigation .previous');
            const nextBtn = DOMUtils.query('.form-navigation .next');

            if (!nextBtn) {
                console.error('Next button not found');
                return;
            }

            if (prevBtn) {
                prevBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.handlePrevious();
                });
            }
            
            nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleNext();
            });
        }

        handlePrevious() {
            const currentIdx = this.getCurrentIndex();
            if (currentIdx > 0) {
                this.updateStepVisuals(currentIdx - 1, 'backward');
                this.navigateTo(currentIdx - 1);
            }
        }

        handleNext() {
            const currentIdx = this.getCurrentIndex();
            const nextIdx = currentIdx + 1;
            
            if (nextIdx >= this.sections.length) {
                return; // Already at last step
            }
            
            // Validate current step
            const isValid = this.validateCurrentStep(currentIdx);
            
            if (isValid) {
                this.navigateTo(nextIdx);
                DOMUtils.removeClass(DOMUtils.queryAll('.field'), 'error');
                this.updateStepVisuals(nextIdx, 'forward');
    } else {
                console.log('Validation failed for step', currentIdx);
            }
        }

        validateCurrentStep(stepIndex) {
            const currentSection = this.sections[stepIndex];
            if (!currentSection) return false;

            // Get all input fields in current step
            const inputs = currentSection.querySelectorAll('input[required], select[required], textarea[required]');
            let isValid = true;

            inputs.forEach(input => {
                const fieldContainer = input.closest('.field');
                let fieldValid = true;

                // Check if field is visible (not hidden by display:none or in hidden section)
                const isVisible = input.offsetParent !== null;
                if (!isVisible) {
                    // Field is hidden, skip validation
                    return;
                }

                // Check required fields
                if (input.hasAttribute('required')) {
                    if (!input.value || input.value.trim() === '') {
                        fieldValid = false;
                    }
                }

                // Validate email format
                if (input.type === 'email' && input.value) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(input.value)) {
                        fieldValid = false;
                    }
                }

                // Validate phone pattern
                if (input.pattern && input.value) {
                    const regex = new RegExp(input.pattern);
                    if (!regex.test(input.value)) {
                        fieldValid = false;
                    }
                }

                // Validate select fields
                if (input.tagName === 'SELECT' && input.hasAttribute('required')) {
                    if (!input.value || input.value === '' || input.value === null) {
                        fieldValid = false;
                    }
                }

                if (!fieldValid) {
                    isValid = false;
                    DOMUtils.addClass(fieldContainer, 'error');
                    
                    // Show error message if Parsley is available
                    if (typeof window.Parsley !== 'undefined' && this.form) {
                        try {
                            // Use stored instance or create new one
                            let parsleyInstance = this.parsleyInstance;
                            if (!parsleyInstance) {
                                const externalLib = window.jQuery;
                                if (externalLib && externalLib.fn && externalLib.fn.parsley) {
                                    parsleyInstance = externalLib(this.form).parsley();
                                } else if (window.Parsley && typeof window.Parsley === 'function') {
                                    parsleyInstance = new window.Parsley(this.form);
                                }
                            }
                            
                            if (parsleyInstance && parsleyInstance.fields) {
                                const field = parsleyInstance.fields.find(f => {
                                    const el = f.$element?.[0] || f.element;
                                    return el === input;
                                });
                                if (field && !field.isValid() && field.validate) {
                                    field.validate();
                                }
                            }
                        } catch (e) {
                            // Parsley error handling failed, continue with native validation
                            console.debug('Parsley validation error:', e);
                        }
                    }
                } else {
                    DOMUtils.removeClass(fieldContainer, 'error');
                }
            });

            return isValid;
        }

        navigateTo(index) {
            console.log('Navigating to step:', index, 'Total sections:', this.sections.length);
            
            this.sections.forEach((section, i) => {
                if (i === index) {
                    DOMUtils.addClass(section, 'current');
                    DOMUtils.show(section);
                } else {
                    DOMUtils.removeClass(section, 'current');
                    DOMUtils.hide(section);
                }
            });

            const prevBtn = DOMUtils.query('.form-navigation .previous');
            const nextBtn = DOMUtils.query('.form-navigation .next');
            const finishBtn = DOMUtils.query('.form-navigation .finish-btn');

            const atTheEnd = index >= this.sections.length - 1;
            console.log('At the end?', atTheEnd, 'Index:', index, 'Sections length:', this.sections.length);
            console.log('Finish button found:', !!finishBtn);
            
            // Show/hide previous button
            if (prevBtn) {
                prevBtn.style.display = index > 0 ? '' : 'none';
            }
            
            // Show/hide next button
            if (nextBtn) {
                nextBtn.style.display = !atTheEnd ? '' : 'none';
                console.log('Next button display:', !atTheEnd ? 'shown' : 'hidden');
            }
            
            // Show/hide finish button (Bestellung Abschliessen)
            if (finishBtn) {
                if (atTheEnd) {
                    // Use DOMUtils.show which now sets display with !important
                    DOMUtils.show(finishBtn, 'block');
                    console.log('Finish button shown, computed display:', window.getComputedStyle(finishBtn).display);
                } else {
                    DOMUtils.hide(finishBtn);
                    console.log('Finish button hidden');
                }
            } else {
                console.error('Finish button not found! Selector: .form-navigation .finish-btn');
                // Try alternative selector
                const altFinishBtn = DOMUtils.query('.finish-btn');
                console.log('Alternative finish button found:', !!altFinishBtn);
                if (altFinishBtn && atTheEnd) {
                    DOMUtils.show(altFinishBtn, 'block');
                }
            }

            this.currentIndex = index;
        }

        getCurrentIndex() {
            const currentSection = this.sections.find(s => s.classList.contains('current'));
            return currentSection ? this.sections.indexOf(currentSection) : 0;
        }

        updateStepVisuals(index, direction) {
            const stepNum = index + 1;
            const isForward = direction === 'forward';

            if (isForward) {
                this.handleForwardStep(index, stepNum);
            } else {
                this.handleBackwardStep(index, stepNum);
            }
        }

        handleForwardStep(index, stepNum) {
            if (index === 1) {
                this.transitionStep(1, 2, 'forward');
            } else if (index === 2) {
                this.transitionStep(2, 3, 'forward');
            }
        }

        handleBackwardStep(index, stepNum) {
            if (index === 0) {
                this.transitionStep(2, 1, 'backward');
            } else if (index === 1) {
                this.transitionStep(3, 2, 'backward');
            }
        }

        transitionStep(fromStep, toStep, direction) {
            const isForward = direction === 'forward';
            const stepItemFrom = DOMUtils.query(`.step-item--${fromStep}`);
            const stepItemTo = DOMUtils.query(`.step-item--${toStep}`);
            const stepFrom = DOMUtils.query(`.step-${fromStep}`);
            const stepTo = DOMUtils.query(`.step-${toStep}`);

            if (isForward) {
                DOMUtils.addClass(stepItemFrom, 'done');
                DOMUtils.hide(stepFrom);
                DOMUtils.show(stepTo);
                DOMUtils.removeClass(DOMUtils.query('.form-navigation'), 'center');
                
                if (toStep === 3) {
                    const finishBtn = DOMUtils.query('.finish-btn');
                    if (finishBtn) {
                        DOMUtils.show(finishBtn);
                    }
                }

                DOMUtils.addClass(stepItemTo, 'active');
                DOMUtils.addClass(stepItemTo, 'water');
            } else {
                DOMUtils.show(stepFrom);
                DOMUtils.hide(stepTo);
                
                if (fromStep === 2) {
                    DOMUtils.addClass(DOMUtils.query('.form-navigation'), 'center');
                } else {
                    DOMUtils.removeClass(DOMUtils.query('.form-navigation'), 'center');
                    const finishBtn = DOMUtils.query('.finish-btn');
                    if (finishBtn) {
                        DOMUtils.hide(finishBtn);
                    }
                }

                DOMUtils.removeClass(stepItemTo, 'water');
                DOMUtils.removeClass(stepItemFrom, 'done');
                DOMUtils.removeClass(stepItemTo, 'active');
            }
        }

        prepareSections() {
            this.sections.forEach((section, index) => {
                // Use valid CSS selector instead of jQuery's :input pseudo-selector
                const inputs = section.querySelectorAll('input, textarea, select, button');
                inputs.forEach(input => {
                    input.setAttribute('data-parsley-group', `block-${index}`);
                });
            });
        }
    }

    // ============================================================================
    // PAYMENT PROCESSOR (Strategy Pattern)
    // ============================================================================

    /**
     * Payment Strategy Interface
     */
    class PaymentStrategy {
        async process(data, subscriberData) {
            throw new Error('Process method must be implemented');
        }
    }

    /**
     * Online Payment Strategy
     */
    class OnlinePaymentStrategy extends PaymentStrategy {
        constructor(stripeKey, returnUrl, animationManager) {
            super();
            this.stripeKey = stripeKey;
            this.returnUrl = returnUrl;
            this.animationManager = animationManager;
        }

        async process(data, subscriberData) {
            if (typeof Stripe === 'undefined') {
                throw new Error('Stripe.js not loaded');
            }

            const stripe = Stripe(this.stripeKey);
            const elements = stripe.elements({ 
                clientSecret: data.clientSecret, 
                appearance: {} 
            });
            const paymentElement = elements.create('payment');
            const form = DOMUtils.query(SELECTORS.PAYMENT_FORM);

            if (!form) throw new Error('Payment form not found');

            paymentElement.mount(SELECTORS.PAYMENT_ELEMENT);

            const submitButton = form.querySelector('.pay-by-card');
            const errorMessage = form.querySelector(SELECTORS.ERROR_MESSAGE);
            let isPaymentElementComplete = false;
            let isSubmitting = false;

            // Do not carry a validation/confirmation error over to a new payment attempt.
            if (errorMessage) {
                errorMessage.textContent = '';
            }

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.setAttribute('aria-disabled', 'true');
            }

            paymentElement.on('change', (event) => {
                isPaymentElementComplete = event.complete;

                // A confirmation error belongs to the previous set of card data.
                // Remove it as soon as Stripe confirms that all current fields are valid.
                if (event.complete && errorMessage) {
                    errorMessage.textContent = '';
                }

                if (submitButton) {
                    const shouldDisable = !isPaymentElementComplete || isSubmitting;
                    submitButton.disabled = shouldDisable;
                    submitButton.setAttribute('aria-disabled', String(shouldDisable));
                }
            });

            const iframe = DOMUtils.query(`${SELECTORS.PAYMENT_ELEMENT} iframe`);
            iframe?.addEventListener('load', () => {
                this.animationManager.hideWaitLoader();
                DOMUtils.removeClass(
                    DOMUtils.queryAll('.popup-payment-method > div[class^="popup__"]'),
                    'card-blur'
                );
            });

            form.addEventListener('submit', async (event) => {
                event.preventDefault();

                if (!isPaymentElementComplete) {
                    return;
                }

                isSubmitting = true;
                if (submitButton) {
                    submitButton.disabled = true;
                    submitButton.setAttribute('aria-disabled', 'true');
                }

                if (errorMessage) {
                    errorMessage.textContent = '';
                }

                try {
                    await this.handleSubmit(stripe, elements, paymentElement, data, subscriberData);
                } finally {
                    isSubmitting = false;
                    if (submitButton) {
                        submitButton.disabled = !isPaymentElementComplete;
                        submitButton.setAttribute('aria-disabled', String(!isPaymentElementComplete));
                    }
                }
            });
        }

        async handleSubmit(stripe, elements, paymentElement, data, subscriberData) {
            const checkoutManager = window.checkoutManager;
            this.animationManager.showWaitLoader();

            try {
                const result = await stripe.confirmPayment({
                    elements,
                    confirmParams: { return_url: this.returnUrl },
                    redirect: 'if_required'
                });

                if (result.error) {
                    const errorMessage = DOMUtils.query(SELECTORS.ERROR_MESSAGE);
                    if (errorMessage) errorMessage.textContent = result.error.message;
                    this.animationManager.hideWaitLoader();
                    // Keep the Stripe iframe interactive after a failed confirmation.
                    // Filtering an iframe ancestor can prevent reliable pointer focus.
                    DOMUtils.removeClass(
                        DOMUtils.queryAll('.popup-payment-method > div[class^="popup__"]'),
                        'card-blur'
                    );
                    requestAnimationFrame(() => paymentElement.focus());
                    return;
                }

                if (result.paymentIntent?.status === "succeeded") {
                    const stripeAmountMinorUnits =
                        typeof result.paymentIntent.amount_received === 'number'
                            ? result.paymentIntent.amount_received
                            : result.paymentIntent.amount;

                    checkoutManager.postPaymentActions(
                        stripeAmountMinorUnits,
                        subscriberData,
                        data.customerId,
                        'online',
                        '',
                        'stripe'
                    );
                }
            } catch (err) {
                console.error("Stripe error:", err);
                this.animationManager.hideWaitLoader();
                DOMUtils.removeClass(
                    DOMUtils.queryAll('.popup-payment-method > div[class^="popup__"]'),
                    'card-blur'
                );
            }
        }
    }

    /**
     * Invoice Payment Strategy
     */
    class InvoicePaymentStrategy extends PaymentStrategy {
        constructor(animationManager) {
            super();
            this.animationManager = animationManager;
        }

        async process(data, subscriberData) {
            this.animationManager.hideWaitLoader();
            this.animationManager.showSuccessLoader();
            
            const checkoutManager = window.checkoutManager;
            checkoutManager.postPaymentActions(
                data.totalAmount,
                subscriberData,
                data.customerId,
                'invoice',
                data.invoiceNumber,
                'api'
            );
        }
    }

    // ============================================================================
    // MAIN CHECKOUT MANAGER
    // ============================================================================

    /**
     * Stripe Checkout Manager - Main orchestrator
     */
    class StripeCheckoutManager {
        constructor() {
            this.apiService = new ApiService(CONFIG.API_BASE_URL);
            this.animationManager = new AnimationManager();
            this.paymentStrategies = {
                online: new OnlinePaymentStrategy(CONFIG.STRIPE_PUBLIC_KEY, CONFIG.RETURN_URL, this.animationManager),
                invoice: new InvoicePaymentStrategy(this.animationManager)
            };
            
            this.elements = {};
            this.currentProductConfig = null;
            this.formNavigation = null;
        }

        async init() {
            console.log('TEST');

            const pid = StringUtils.getUrlParameter('pid');
            if (!pid) {
                window.location.replace("https://mrgrien.com");
                return;
            }

            console.log('Initializing Stripe Checkout Manager...');
            this.currentProductConfig = PRODUCT_CONFIG[pid] || null;
            this.cacheElements();
            
            // Verify critical elements exist
            if (!this.elements.checkoutForm) {
                console.error('Checkout form not found!');
                return;
            }
            
            if (!this.elements.zipSelectShipping || !this.elements.zipSelectBilling) {
                console.error('Zip select elements not found!');
            }
            
            this.initializeSelect2();
            this.setupFormNavigation();
            this.setupAddressToggle();
            this.setupAgreementCheckbox();
            this.setupFinishButton();
            this.setupPopupClose();
            
            // Load data immediately on page load
            console.log('Loading postal cities and prices...');
            try {
                await Promise.all([
                    this.loadPostalCities(),
                    this.loadPrices()
                ]);
                console.log('All data loaded successfully');
            } catch (error) {
                console.error('Error loading initial data:', error);
            }
        }

        cacheElements() {
            this.elements = {
                zipSelectShipping: DOMUtils.query(SELECTORS.ZIP_SHIPPING),
                zipSelectBilling: DOMUtils.query(SELECTORS.ZIP_BILLING),
                cityInputShipping: DOMUtils.query(SELECTORS.CITY_SHIPPING),
                cityInputBilling: DOMUtils.query(SELECTORS.CITY_BILLING),
                invalidZipMessage: DOMUtils.query(SELECTORS.INVALID_ZIP),
                selectBilling: DOMUtils.query(SELECTORS.SELECT_BILLING),
                formNavigationBtn: DOMUtils.query(SELECTORS.FORM_NAV_BTN),
                checkoutForm: DOMUtils.query(SELECTORS.CHECKOUT_FORM)
            };
        }

        initializeSelect2() {
            const externalLib = window.jQuery;
            if (!externalLib?.fn?.select2?.amd) return;

            const Defaults = externalLib.fn.select2.amd.require('select2/defaults');
            externalLib.extend(Defaults.defaults, { searchInputPlaceholder: '' });

            const SearchDropdown = externalLib.fn.select2.amd.require('select2/dropdown/search');
            const originalRender = SearchDropdown.prototype.render;

            SearchDropdown.prototype.render = function (decorated) {
                const rendered = originalRender.apply(this, Array.prototype.slice.apply(arguments));
                this.$search.attr('placeholder', this.options.get('searchInputPlaceholder'));
                return rendered;
            };
        }

        validateZipCode(zipCode) {
            return this.currentProductConfig?.validZips?.has(zipCode) ?? true;
        }

        handleZipSelection(selectElement, isShipping) {
            const selectedZip = selectElement.value;
            const selectedOption = selectElement.options[selectElement.selectedIndex];
            const city = selectedOption?.getAttribute('data-city') || '';

            if (isShipping) {
                this.elements.cityInputShipping.value = city;
        } else {
                this.elements.cityInputBilling.value = city;
            }

            if (isShipping && this.currentProductConfig) {
                const isValid = this.validateZipCode(selectedZip);
                const zipField = selectElement.closest('.field');
                const navBtn = this.elements.formNavigationBtn;

                if (isValid) {
                    DOMUtils.removeClass(zipField, 'error');
                    DOMUtils.fadeOut(this.elements.invalidZipMessage);
                    DOMUtils.fadeIn(this.elements.selectBilling, { display: 'block' });
                    navBtn?.removeAttribute('disabled');
                } else {
                    DOMUtils.addClass(zipField, 'error');
                    this.elements.invalidZipMessage.style.display = 'flex';
                    DOMUtils.fadeIn(this.elements.invalidZipMessage);
                    DOMUtils.fadeOut(this.elements.selectBilling);
                    navBtn?.setAttribute('disabled', 'disabled');
                }
            }
        }

        async loadPostalCities() {
            try {
                console.log('Loading postal cities from API...');
                console.log('API Base URL:', this.apiService.baseUrl);
                
                const data = await this.apiService.getPostalCities();
                
                if (!data) {
                    console.error('No data received from API');
                    return;
                }
                
                console.log('API Response:', data);
                
                if (!data.success) {
                    console.error('API returned success=false:', data);
                    return;
                }

                if (!data.data || !Array.isArray(data.data)) {
                    console.error('Invalid postal cities data format. Expected data.data to be an array:', data);
                    return;
                }

                if (data.data.length === 0) {
                    console.warn('No postal cities returned from API');
                    return;
                }

                const options = data.data.map(item => {
                    if (!item.zip || !item.city) {
                        console.warn('Invalid postal city item:', item);
                        return '';
                    }
                    return `<option value="${item.zip}" data-city="${item.city}">${item.zip}</option>`;
                }).filter(opt => opt !== '').join('');

                if (!this.elements.zipSelectShipping || !this.elements.zipSelectBilling) {
                    console.error('Zip select elements not found. Shipping:', !!this.elements.zipSelectShipping, 'Billing:', !!this.elements.zipSelectBilling);
                    return;
                }

                this.elements.zipSelectShipping.insertAdjacentHTML('beforeend', options);
                this.elements.zipSelectBilling.insertAdjacentHTML('beforeend', options);

                console.log(`Successfully loaded ${data.data.length} postal cities`);
                
                // Reinitialize Select2 if available
                this.initializeSelect2Handlers();
                this.setZipFromUrl();
            } catch (error) {
                console.error('Error loading postal cities:', error);
                console.error('Error stack:', error.stack);
            }
        }

        initializeSelect2Handlers() {
            const externalLib = window.jQuery;
            if (externalLib?.fn?.select2) {
                externalLib(this.elements.zipSelectShipping).select2({ searchInputPlaceholder: 'Bitte wählen' });
                externalLib(this.elements.zipSelectBilling).select2({ searchInputPlaceholder: 'Bitte wählen' });
                externalLib(this.elements.zipSelectShipping).on("select2:select", () => {
                    this.handleZipSelection(this.elements.zipSelectShipping, true);
                });
                externalLib(this.elements.zipSelectBilling).on("select2:select", () => {
                    this.handleZipSelection(this.elements.zipSelectBilling, false);
                });
            } else {
                this.elements.zipSelectShipping.addEventListener('change', () => {
                    this.handleZipSelection(this.elements.zipSelectShipping, true);
                });
                this.elements.zipSelectBilling.addEventListener('change', () => {
                    this.handleZipSelection(this.elements.zipSelectBilling, false);
                });
            }
        }

        setZipFromUrl() {
            const zipFromUrl = StringUtils.getUrlParameter('zip');
            if (zipFromUrl) {
                this.elements.zipSelectShipping.value = zipFromUrl;
                DOMUtils.triggerEvent(this.elements.zipSelectShipping, 'change');
                this.elements.zipSelectBilling.value = zipFromUrl;
                DOMUtils.triggerEvent(this.elements.zipSelectBilling, 'change');
            }
        }

        async loadPrices() {
            const pid = StringUtils.getUrlParameter('pid');
            if (!pid) return;

            try {
                const data = await this.apiService.getPrices(pid);
                if (!data.success || !data.prices) return;

                const pricesList = DOMUtils.query(SELECTORS.PRICES_LIST);
                if (!pricesList) return;

                pricesList.innerHTML = this.buildPricesHtml(data.prices);
                this.updateProductInfo(data);
                this.setupPricesLogic();
            } catch (error) {
                console.error('Error loading prices:', error);
            }
        }

        buildPricesHtml(prices) {
            return prices.map(item => {
                const { intervalTitle, checked } = this.getIntervalInfo(item);
                const slug = StringUtils.slugify(intervalTitle);
                return `
                    <div class="field--radio">
                        <input type="radio" id="${slug}" ${checked} class="radio-input" 
                               name="payment-frequency-group" value="${item.id}" data-amount="${item.amount}"
                               data-interval="${item.interval || ''}" data-interval-count="${item.interval_count ?? ''}">
                        <label for="${slug}" class="radio-label">
                            <span class="radio-border"></span>
                            <div class="radio-label-info">
                                <span>${intervalTitle}</span>
                                <div class="radio-label__info">${item.amount} ${item.currency}</div>
                            </div>
                        </label>
                    </div>
                `;
            }).join('');
        }

        getIntervalInfo(item) {
            if (item.interval === "year") {
                return { intervalTitle: "Jährlich", checked: '' };
            } else if (item.interval === "month" && item.interval_count === 1) {
                return { intervalTitle: "Monatlich", checked: 'checked' };
            } else if (item.interval === "month" && item.interval_count === 3) {
                return { intervalTitle: "Alle 3 Monate", checked: '' };
            }
            return { intervalTitle: "", checked: '' };
        }

        updateProductInfo(data) {
            const productTitle = this.currentProductConfig?.productTitle || data.pname || '';
            const productNameEl = DOMUtils.query('.checkout-product .checkout-product__name');
            if (productNameEl) productNameEl.textContent = productTitle;
            
            if (data.pimg) {
                const productImg = DOMUtils.query('.checkout-product .checkout-product__img > img');
                if (productImg) productImg.setAttribute('src', data.pimg);
            }
        }

        setupPricesLogic() {
            const paymentMethod1 = DOMUtils.query(SELECTORS.PAYMENT_METHOD_1);
            if (!paymentMethod1) return;

            const paymentMethod1Field = paymentMethod1.closest('.field--radio');

            const setInvoiceAvailability = (allowInvoice) => {
                if (!paymentMethod1Field) return;

                if (allowInvoice) {
                    DOMUtils.removeClass(paymentMethod1Field, 'disabled');
                    paymentMethod1.disabled = false;
                    paymentMethod1.removeAttribute('disabled');
                } else {
                    paymentMethod1.checked = false;
                    DOMUtils.addClass(paymentMethod1Field, 'disabled');
                    paymentMethod1.disabled = true;
                    paymentMethod1.setAttribute('disabled', 'true');
                }
            };

            const isYearlyOrQuarterly = (radioEl) => {
                if (!radioEl) return false;
                const interval = radioEl.dataset?.interval;
                const intervalCountRaw = radioEl.dataset?.intervalCount;
                const intervalCount = intervalCountRaw === '' || intervalCountRaw === undefined ? null : parseInt(intervalCountRaw, 10);

                return interval === 'year' || (interval === 'month' && intervalCount === 3);
            };

            // Apply initial state based on the currently selected frequency
            const initiallySelected = DOMUtils.query('input[type=radio][name=payment-frequency-group]:checked');
            setInvoiceAvailability(isYearlyOrQuarterly(initiallySelected));

            DOMUtils.queryAll('input[type=radio][name=payment-frequency-group]').forEach(radio => {
                radio.addEventListener('change', () => {
                    if (!radio.checked) return;
                    setInvoiceAvailability(isYearlyOrQuarterly(radio));
                });
            });
        }

        setupFormNavigation() {
            this.formNavigation = new FormNavigationManager(this.elements.checkoutForm);
        }

        setupAddressToggle() {
            // Find radio buttons by their class or name attribute
            const otherAddressRadio = DOMUtils.query('input[name="address_type"][value="shipping_neq_billing"]');
            const oneAddressRadio = DOMUtils.query('input[name="address_type"][value="shipping_eq_billing"]');
            const otherAddress = DOMUtils.query(SELECTORS.OTHER_ADDRESS);
            const otherAddressInner = DOMUtils.query(SELECTORS.OTHER_ADDRESS_INNER);

            if (!otherAddressRadio || !oneAddressRadio) {
                console.error('Address radio buttons not found');
                // Try alternative selectors
                const altOther = DOMUtils.query(SELECTORS.OTHER_ADDRESS_BTN);
                const altOne = DOMUtils.query(SELECTORS.ONE_ADDRESS_BTN);
                if (!altOther || !altOne) {
                    console.error('Alternative address toggle elements not found');
                    return;
                }
            }

            if (!otherAddress || !otherAddressInner) {
                console.error('Other address container elements not found');
                return;
            }

            const handleOtherAddress = () => {
                console.log('Showing other address form');
                DOMUtils.show(otherAddress);
                DOMUtils.slideDown(otherAddress);
            setTimeout(() => {
                    otherAddressInner.style.opacity = '1';
                    FormUtils.setRequired(REQUIRED_FIELDS, true);
                }, 200);
            };

            const handleOneAddress = () => {
                console.log('Hiding other address form');
                otherAddressInner.style.opacity = '0';
                setTimeout(() => {
                    DOMUtils.slideUp(otherAddress);
                    setTimeout(() => {
                        DOMUtils.hide(otherAddress);
                        otherAddress.querySelectorAll('input').forEach(input => input.value = '');
                        DOMUtils.removeClass(otherAddress.querySelectorAll('.field'), 'error');
                        FormUtils.setRequired(REQUIRED_FIELDS, false);
                    }, 300);
                }, 200);
            };

            // Listen to change events on radio buttons
            const radioButtons = DOMUtils.queryAll('input[name="address_type"]');
            console.log('Found radio buttons:', radioButtons.length);
            
            radioButtons.forEach(radio => {
                radio.addEventListener('change', (e) => {
                    console.log('Radio button changed:', e.target.value, e.target.checked);
                    if (e.target.value === 'shipping_neq_billing' && e.target.checked) {
                        handleOtherAddress();
                    } else if (e.target.value === 'shipping_eq_billing' && e.target.checked) {
                        handleOneAddress();
                    }
                });
            });

            // Also handle clicks on labels (for better UX)
            if (otherAddressRadio) {
                const otherAddressLabel = otherAddressRadio.closest('.field--radio')?.querySelector('label');
                if (otherAddressLabel) {
                    otherAddressLabel.addEventListener('click', () => {
                        setTimeout(() => {
                            if (otherAddressRadio.checked) handleOtherAddress();
                        }, 10);
                    });
                }
            }

            if (oneAddressRadio) {
                const oneAddressLabel = oneAddressRadio.closest('.field--radio')?.querySelector('label');
                if (oneAddressLabel) {
                    oneAddressLabel.addEventListener('click', () => {
                        setTimeout(() => {
                            if (oneAddressRadio.checked) handleOneAddress();
                        }, 10);
                    });
                }
            }
            
            // Check initial state
            if (otherAddressRadio && otherAddressRadio.checked) {
                handleOtherAddress();
            }
        }

        setupAgreementCheckbox() {
            const agreeBtn = DOMUtils.query(SELECTORS.AGREE_BTN);
            const finishBtn = DOMUtils.query(SELECTORS.FINISH_BTN);

            if (!agreeBtn || !finishBtn) return;

            if (agreeBtn.checked) {
                DOMUtils.removeClass(finishBtn, 'pe-none');
            }

            agreeBtn.addEventListener('change', () => {
                DOMUtils[agreeBtn.checked ? 'removeClass' : 'addClass'](finishBtn, 'pe-none');
            });
        }

        async setupFinishButton() {
            const finishBtn = DOMUtils.query(SELECTORS.FINISH_BTN);
            if (!finishBtn) return;

            finishBtn.addEventListener('click', async () => {
                await this.handleFinishClick();
            });
        }

        normalizeErrorMessage(message) {
            if (!message) return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
            
            const messageLower = message.toLowerCase();
            
            // Check for customer already exists error
            if (messageLower.includes('customer already exists')) {
                return 'Unter dieser E-Mail-Adresse existiert bereits ein Abo. Bitte kontaktieren Sie uns, wenn Sie mehr als ein Abo unter einer E-Mail-Adresse einrichten möchten.';
            }
            
            return message;
        }

        showError(message) {
            const errorElement = DOMUtils.query('#checkout-error-message');
            if (errorElement) {
                const normalizedMessage = this.normalizeErrorMessage(message);
                errorElement.textContent = normalizedMessage;
                DOMUtils.show(errorElement);
                // Scroll to error message
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        hideError() {
            const errorElement = DOMUtils.query('#checkout-error-message');
            if (errorElement) {
                DOMUtils.hide(errorElement);
                errorElement.textContent = '';
            }
        }

        async handleFinishClick() {
            this.hideError();
            this.blurCheckoutUI();
            this.animationManager.showWaitLoader();

            const subscriberData = FormUtils.serialize(this.elements.checkoutForm);
            const paymentMethod2 = DOMUtils.query(SELECTORS.PAYMENT_METHOD_2);
            const paymentMethod1 = DOMUtils.query(SELECTORS.PAYMENT_METHOD_1);
            const isOnline = paymentMethod2?.checked;
            const isInvoice = paymentMethod1?.checked;

            if (!isOnline && !isInvoice) {
                this.animationManager.hideWaitLoader();
                this.unblurCheckoutUI();
                return;
            }

            try {
                const data = await this.apiService.processCheckout(subscriberData);
                if (!data.success) {
                    this.animationManager.hideWaitLoader();
                    this.unblurCheckoutUI();
                    const errorMessage = data.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
                    this.showError(errorMessage);
                    return;
                }

                // Zero-amount invoice (e.g. 100% voucher): no PaymentIntent exists, so clientSecret is null.
                // Skip the Stripe payment popup and go straight to the success screen.
                if (isOnline && (data.totalAmount === 0 || !data.clientSecret)) {
                    this.animationManager.hideWaitLoader();
                    this.animationManager.showSuccessLoader();
                    this.postPaymentActions(0, subscriberData, data.customerId, 'online', '', 'api');
                    return;
                }

                const strategy = isOnline ? this.paymentStrategies.online : this.paymentStrategies.invoice;
                await strategy.process(data, subscriberData);

                if (isOnline) {
                    const popupPayment = DOMUtils.query('.popup-payment-method');
                    if (popupPayment) {
                        // Lock body scroll
                        document.body.classList.add('popup-open');
                        // Store scroll position
                        const scrollY = window.scrollY;
                        document.body.style.top = `-${scrollY}px`;
                        DOMUtils.fadeIn(popupPayment, { display: 'flex' });
                    }
                }
            } catch (error) {
                console.error('Error processing checkout:', error);
                this.animationManager.hideWaitLoader();
                this.unblurCheckoutUI();
                
                // Extract error message from error object
                let errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
                if (error.message) {
                    errorMessage = error.message;
                } else if (error.data?.message) {
                    errorMessage = error.data.message;
                }
                
                this.showError(errorMessage);
            }
        }

        setupPopupClose() {
            const closePopupBtn = DOMUtils.query(SELECTORS.CLOSE_POPUP);
            if (!closePopupBtn) return;

            const closePopup = () => {
                // Unlock body scroll
                const scrollY = document.body.style.top;
                document.body.classList.remove('popup-open');
                document.body.style.top = '';
                if (scrollY) {
                    window.scrollTo(0, parseInt(scrollY || '0') * -1);
                }
                
                DOMUtils.removeClass(
                    DOMUtils.queryAll('#checkoutForm, .section-checkout > div > .steps'),
                    'card-blur'
                );
                const paymentMethod2 = DOMUtils.query(SELECTORS.PAYMENT_METHOD_2);
                if (paymentMethod2) paymentMethod2.checked = false;
                const errorMessage = DOMUtils.query(SELECTORS.ERROR_MESSAGE);
                if (errorMessage) errorMessage.textContent = '';
                const popup = closePopupBtn.closest('.popup');
                if (popup) DOMUtils.hide(popup);
            };

            closePopupBtn.addEventListener('click', closePopup);
            
            // Also close on overlay click
            const popupOverlay = DOMUtils.query('.popup-payment-method');
            if (popupOverlay) {
                popupOverlay.addEventListener('click', (e) => {
                    if (e.target === popupOverlay) {
                        closePopup();
                    }
                });
            }
        }

        blurCheckoutUI() {
            DOMUtils.addClass(
                DOMUtils.queryAll('#checkoutForm, .section-checkout > div > .steps, .popup-payment-method > div[class^="popup__"]'),
                'card-blur'
            );
        }

        unblurCheckoutUI() {
            DOMUtils.removeClass(
                DOMUtils.queryAll('#checkoutForm, .section-checkout > div > .steps, .popup-payment-method > div[class^="popup__"]'),
                'card-blur'
            );
        }

        postPaymentActions(amount, subscriberData, customerId, method, invoiceNumber = '', amountSource = 'unknown') {
            const updates = this.parseFormData(subscriberData, amount, method, invoiceNumber, amountSource);
            this.applyUpdates(updates, method, customerId);
        }

        formatPrice(amount, options = {}) {
            const minorUnits = this.toMinorUnits(amount, options);
            return this.formatMinorUnits(minorUnits);
        }

        formatMinorUnits(minorUnits) {
            const safeMinorUnits = Number.isFinite(minorUnits) ? minorUnits : 0;
            const majorUnits = safeMinorUnits / 100;

            try {
                // Swiss formatting (decimal separator ".")
                return new Intl.NumberFormat('de-CH', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(majorUnits);
            } catch (_) {
                return majorUnits.toFixed(2);
            }
        }

        /**
         * Normalize an "amount" into Stripe-style minor units (e.g. CHF 3.59 -> 359).
         * Sources:
         * - **stripe**: always already minor units (integers).
         * - **api**: often already major units ("3.59"), but may vary; handle robustly.
         */
        toMinorUnits(amount, options = {}) {
            const source = options?.source || 'unknown';

            if (amount === null || amount === undefined || amount === '') return 0;

            if (typeof amount === 'number') {
                if (source === 'stripe') return Math.round(amount);
                if (!Number.isFinite(amount)) return 0;
                if (!Number.isInteger(amount)) return Math.round(amount * 100);

                // Heuristic for integer numbers from non-Stripe sources:
                // - small integers are likely major units (e.g. 30 => CHF 30.00)
                // - larger integers are likely minor units (e.g. 359 => CHF 3.59, 3000 => CHF 30.00)
                return amount < 100 ? amount * 100 : amount;
            }

            if (typeof amount === 'string') {
                const raw = amount.trim();
                if (!raw) return 0;

                // If it contains a decimal separator, treat as major units.
                if (raw.includes('.') || raw.includes(',')) {
                    const normalized = raw
                        .replace(/\s+/g, '')
                        .replace(/'/g, '')
                        .replace(',', '.')
                        .replace(/[^\d.\-]/g, '');
                    const major = Number(normalized);
                    return Number.isFinite(major) ? Math.round(major * 100) : 0;
                }

                // No decimal separator: integer-like string.
                const intVal = parseInt(raw.replace(/[^\d\-]/g, ''), 10);
                if (!Number.isFinite(intVal)) return 0;

                if (source === 'stripe') return intVal;

                // For API/unknown, interpret small ints as major units.
                return intVal < 100 ? intVal * 100 : intVal;
            }

            return 0;
        }

        parseFormData(subscriberData, amount, method, invoiceNumber, amountSource) {
            const formData = new URLSearchParams(subscriberData);
            const updates = {};

            for (const [field, value] of formData.entries()) {
            switch (field) {
                case 'address_type':
                        if (value === 'shipping_neq_billing') updates.showShippingNeqBilling = true;
                    break;
                case 'payment-frequency-group':
                        updates.paymentFrequency = this.getPaymentFrequencyText(value);
                    break;
                case 'payment-method-group':
                    if (method === 'online') {
                            updates.finalPrice = this.formatPrice(amount, { source: amountSource });
                    } else {
                            updates.hideCardLand = true;
                            updates.finalPrice = this.formatPrice(amount, { source: amountSource });
                            updates.paymentType = 'Kauf auf Rechnung';
                            updates.invoiceNumber = invoiceNumber;
                    }
                    break;
                case 'discount_code':
                        if (value) updates.discountCode = value;
                    break;
                case 'shipping_company':
                    if (value) {
                            updates.shippingCompany = value;
                            updates.showShippingCompany = true;
                    }
                    break;
                case 'billing_company':
                    if (value) {
                            updates.billingCompany = value;
                            updates.showBillingCompany = true;
                    }
                    break;
                default:
                        updates[field] = value;
                }
            }
            return updates;
        }

        getPaymentFrequencyText(value) {
            const freqRadio = DOMUtils.queryAll('[name="payment-frequency-group"]')
                .find(radio => radio.value === value);
            if (!freqRadio) return '';

            const field = freqRadio.closest('.field--radio');
            const label = field?.querySelector(`label[for="${freqRadio.id}"]`) || freqRadio.nextElementSibling;

            return label?.querySelector('.radio-label-info span')?.textContent?.trim() || '';
        }

        applyUpdates(updates, method, customerId) {
            const updateMap = {
                showShippingNeqBilling: () => DOMUtils.show(DOMUtils.query('.shipping-neq-billing')),
                paymentFrequency: () => {
                    const el = DOMUtils.query('[data-uin="payment-frequency-group"]');
                    if (el) el.textContent = updates.paymentFrequency;
                },
                finalPrice: () => {
                    const el = DOMUtils.query('[data-uin="final_payment_price"] > b');
                    if (el) el.textContent = updates.finalPrice;
                },
                hideCardLand: () => {
                    const row = DOMUtils.query('[data-uin="card_land"]')?.closest('.checkout-info__row');
                    if (row) DOMUtils.hide(row);
                },
                paymentType: () => {
                    const el = DOMUtils.query('[data-uin="payment_type"]');
                    if (el) el.textContent = updates.paymentType;
                },
                invoiceNumber: () => {
                    const el = DOMUtils.query('[data-uin="invoice_number"]');
                    if (el) {
                        el.closest('.checkout-info__row')?.classList.add('invoice-login-cta-row');
                        el.classList.add('invoice-login-cta-wrapper');
                        el.innerHTML = '<div class="invoice-login-cta">' +
                            '<p class="invoice-login-cta__text">Sie können Ihre Rechnungen jederzeit im Login-Bereich Ihres Kundenkonto einsehen und herunterladen.</p>' +
                            '<a href="/pages/user-login" class="invoice-login-cta__btn">Zum Login-Bereich</a>' +
                            '</div>';
                    }
                },
                discountCode: () => {
                    const span = DOMUtils.query('[data-uin="discount_code"] > span');
                    const el = DOMUtils.query('[data-uin="discount_code"]');
                    if (span) span.textContent = updates.discountCode;
                    if (el) DOMUtils.show(el);
                },
                shippingCompany: () => {
                    const el = DOMUtils.query('[data-uin="shipping_company"]');
                    if (el) el.textContent = updates.shippingCompany;
                    if (updates.showShippingCompany) {
                        DOMUtils.show(DOMUtils.query('.shipping-eq-billing .checkout-info__company'));
                    }
                },
                billingCompany: () => {
                    const el = DOMUtils.query('[data-uin="billing_company"]');
                    if (el) el.textContent = updates.billingCompany;
                    if (updates.showBillingCompany) {
                        DOMUtils.show(DOMUtils.query('.shipping-neq-billing .checkout-info__company'));
                    }
                }
            };

            Object.keys(updates).forEach(key => {
                if (updateMap[key]) {
                    updateMap[key]();
                } else if (!['showShippingNeqBilling', 'paymentFrequency', 'finalPrice', 'hideCardLand',
                             'paymentType', 'invoiceNumber', 'discountCode', 'shippingCompany',
                             'billingCompany', 'showShippingCompany', 'showBillingCompany'].includes(key)) {
                    const target = DOMUtils.query(`[data-uin="${key}"]`);
                    if (target) target.textContent = updates[key];
                }
            });

            if (method === 'online') {
                this.fetchCardData(customerId);
            }

            this.showSuccessScreen();
        }

        showSuccessScreen() {
            // Unlock body scroll (remove popup-open class if it exists)
            const scrollY = document.body.style.top;
            document.body.classList.remove('popup-open');
            document.body.style.top = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
            
            // Hide all loaders
            this.animationManager.hideWaitLoader();
            this.animationManager.hideSuccessLoader();
            
            // Hide form elements and show success screen
            DOMUtils.hide(DOMUtils.queryAll('.popup-payment-method, .section-checkout > div > .steps, .checkout-form-title, .form-navigation, .step-3, #invoice-loader-new, #wait-loader, #success-loader'));
            DOMUtils.removeClass(DOMUtils.queryAll('#checkoutForm, .section-checkout > div > .steps'), 'card-blur');
            DOMUtils.show(DOMUtils.queryAll('.last-step-info, .step-finish'));
        }

        async fetchCardData(customerId) {
            try {
                const res = await this.apiService.getPaymentMethod(customerId);
                if (res.success && res.data) {
                    const { brand, country, cardNumber } = res.data;
                    const cardInfoEl = DOMUtils.query('[data-uin="card_info"]');
                    const cardLandEl = DOMUtils.query('[data-uin="card_land"]');
                    if (cardInfoEl) cardInfoEl.textContent = `${brand}, **** **** **** ${cardNumber}`;
                    if (cardLandEl) cardLandEl.textContent = country;
                }
            } catch (error) {
                console.error('Error fetching card data:', error);
            }
        }
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    async function init() {
        window.checkoutManager = new StripeCheckoutManager();
        await window.checkoutManager.init();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})(window, document);
